/**
 * components/landing/contact/data.js — Phase 9 · Contact & Sales copy.
 * All strings live here so PRD tweaks happen in one place.
 */

// ── §9.2 Choose Your Enquiry (5 interactive cards) ────────────────────
export const ENQUIRY_TYPES = [
  {
    id: 'sales', label: 'Sales', iconKey: 'Handshake', tone: 'orange', anchor: '#sales',
    body:   'Product enquiries, licensing, pricing and white-label solutions.',
    for:     ['Product enquiries', 'Licensing', 'Pricing', 'White-Label solutions'],
    cta:    'Talk to sales',
  },
  {
    id: 'demo',  label: 'Product Demo', iconKey: 'Calendar', tone: 'green', anchor: '#demo', primary: true,
    body:   'Live platform walkthroughs and evaluation sessions for your team.',
    for:     ['Platform walkthroughs', 'Live demonstrations', 'Business presentations', 'Evaluation sessions'],
    cta:    'Book a demo',
  },
  {
    id: 'support', label: 'Technical Support', iconKey: 'LifeBuoy', tone: 'blue', anchor: '#support',
    body:   'Existing customers — platform issues, billing questions, workspace assistance.',
    for:     ['Existing customers', 'Platform issues', 'Billing questions', 'Workspace assistance'],
    cta:    'Get support',
  },
  {
    id: 'partnerships', label: 'Partnerships', iconKey: 'Puzzle', tone: 'purple', anchor: '#partnerships',
    body:   'Reseller, integration, educational and strategic collaboration.',
    for:     ['Strategic partnerships', 'Reseller opportunities', 'Technology integrations', 'Educational collaborations'],
    cta:    'Discuss partnership',
  },
  {
    id: 'consulting', label: 'Enterprise Consulting', iconKey: 'Compass', tone: 'cyan', anchor: '#consulting',
    body:   'Large-scale deployments, transformation and multi-workspace licensing.',
    for:     ['Large-scale deployments', 'Business transformation', 'Custom implementation planning', 'Multi-workspace licensing'],
    cta:    'Speak with an expert',
  },
]

// ── §9.3 Demo topics (5 pre-defined evaluation flows) ──────────────────
export const DEMO_TOPICS = [
  { id: 'overview',     label: 'Platform Overview',   iconKey: 'Sparkles',      tone: 'orange', body: 'A guided end-to-end tour of the entire Kalki Ai Cloud platform.', mins: 45 },
  { id: 'ai',           label: 'AI Platform',          iconKey: 'Bot',          tone: 'orange', body: 'Grounded RAG mentor, prompt engine and knowledge base configuration.', mins: 30 },
  { id: 'trading',      label: 'Trading Platform',     iconKey: 'LineChart',    tone: 'green',  body: 'Terminal, strategy builder, backtesting and portfolio deep-dive.', mins: 45 },
  { id: 'wl',           label: 'White-Label Business', iconKey: 'Building2',    tone: 'cyan',   body: 'Provisioning, branding, custom domains and workspace management.', mins: 45 },
  { id: 'licensing',    label: 'Enterprise Licensing', iconKey: 'ShieldCheck',  tone: 'purple', body: 'Commercial model, plan comparison, ROI narrative and procurement.', mins: 30 },
]

export const DEMO_SLOTS = ['09:00', '11:00', '14:00', '16:00']

// ── §9.4 Sales enquiry form dropdown options ──────────────────────────
export const INDUSTRIES = [
  'Trading & Investment',
  'Financial Education',
  'Broker / Dealer',
  'Wealth Management',
  'FinTech',
  'Consultancy',
  'Community / Media',
  'Other',
]

export const COMPANY_SIZES = [
  '1–10 employees',
  '11–50 employees',
  '51–200 employees',
  '201–1,000 employees',
  '1,000+ employees',
]

export const USER_ESTIMATES = [
  'Up to 100 users',
  '100–1,000 users',
  '1,000–10,000 users',
  '10,000+ users',
]

export const LICENSE_OPTIONS = [
  { id: 'starter',      label: 'Starter — $25,000 / 3 months',    tone: 'orange' },
  { id: 'professional', label: 'Professional — $40,000 / 6 mo',   tone: 'orange' },
  { id: 'enterprise',   label: 'Enterprise — $60,000 / year',      tone: 'green'  },
  { id: 'undecided',    label: 'Not sure yet',                    tone: 'purple' },
]

export const TIMELINES = [
  'This month',
  'Next 1–3 months',
  'Next 3–6 months',
  'Beyond 6 months',
  'Exploring only',
]

export const REGIONS = [
  'North America',
  'Latin America',
  'Europe',
  'Middle East & Africa',
  'Asia Pacific',
  'India',
  'Global (multi-region)',
]

// ── §9.5 Technical Support quick-actions ─────────────────────────────
// Cards with `modal: true` open the inline support form (see
// components/landing/contact/sections/Support.jsx); others link to
// their in-page anchor on /resources.
export const SUPPORT_ACTIONS = [
  { id: 'ticket',   label: 'Submit a support request',  iconKey: 'TicketCheck', tone: 'orange', body: 'Open a support request and our team will triage it based on your plan tier.', ctaLabel: 'Open form',    modal: true },
  { id: 'docs',     label: 'Browse documentation',       iconKey: 'BookOpen',   tone: 'blue',   body: 'Full platform reference organised by module — with predictive search.',       href: '/resources#documentation', ctaLabel: 'Open docs' },
  { id: 'kb',       label: 'Search the knowledge base',   iconKey: 'HelpCircle', tone: 'purple', body: 'Popular answers ranked by intent across seven top-level categories.',        href: '/resources#kb',            ctaLabel: 'Search KB' },
  { id: 'billing',  label: 'Billing assistance',           iconKey: 'CreditCard', tone: 'green',  body: 'Invoices, tax IDs, prorated upgrades, refunds and renewal handling.',        href: '/resources#faqs',          ctaLabel: 'Billing FAQ' },
  { id: 'issue',    label: 'Report a technical issue',      iconKey: 'AlertTriangle', tone: 'rose', body: 'Something broken?  Send us the reproduction steps — we prioritise every report.', ctaLabel: 'Report issue', modal: true },
  { id: 'portal',   label: 'Customer portal enquiry',       iconKey: 'LogIn',      tone: 'cyan',   body: 'Need help accessing your workspace or a lost invite?  Send us a note and we\u2019ll get you back in.', ctaLabel: 'Contact us',   modal: true },
]

// ── §9.6 Partnership categories (5) ─────────────────────────────────
export const PARTNERSHIPS = [
  { id: 'tech',     label: 'Technology Partners',   iconKey: 'Cpu',           tone: 'orange', body: 'Integrate execution venues, data feeds, model providers and observability stacks.' },
  { id: 'channel',  label: 'Channel Partners',       iconKey: 'Handshake',    tone: 'green',  body: 'Resell Kalki Ai Cloud under your existing enterprise motion — with margin and enablement.' },
  { id: 'edu',      label: 'Education Partners',      iconKey: 'GraduationCap',tone: 'purple', body: 'Bundle white-labelled academies and certification tracks with your programmes.' },
  { id: 'int',      label: 'Integration Partners',     iconKey: 'Puzzle',      tone: 'blue',   body: 'Ship first-party integrations — CRM, BI, notifications, observability, ticketing.' },
  { id: 'strat',    label: 'Strategic Alliances',       iconKey: 'Compass',    tone: 'cyan',   body: 'Co-develop new markets, verticals and joint go-to-market with mutual commit.' },
]

// ── §9.7 Enterprise Consulting focus areas (5) ─────────────────────────
export const CONSULTING = [
  { id: 'plan',      label: 'Platform planning',              iconKey: 'ClipboardList', tone: 'orange', body: 'Map your business model, target customers and platform surface to a phased rollout.' },
  { id: 'wl',        label: 'White-Label rollout',             iconKey: 'Building2',    tone: 'cyan',   body: 'Brand, tenant, domain, RBAC and pricing architecture, delivered as a runnable playbook.' },
  { id: 'business',  label: 'Business process consultation',    iconKey: 'Workflow',    tone: 'blue',   body: 'Design workflows for sales, onboarding, education and support inside the platform.' },
  { id: 'onboard',   label: 'Team onboarding',                   iconKey: 'UsersRound',   tone: 'green',  body: 'Structured trainings across product, admin, mentor and support disciplines.' },
  { id: 'growth',    label: 'Growth strategy',                    iconKey: 'TrendingUp',  tone: 'purple', body: 'Turn platform adoption into recurring revenue — pricing, expansion, retention.' },
]

// ── §9.8 Global Contact channels ─────────────────────────────────────
export const CONTACT_CHANNELS = [
  { id: 'sales',   label: 'Sales',             iconKey: 'Mail',      tone: 'orange', value: 'sales@kalkiai.trade',    hint: 'Product, licensing & pricing' },
  { id: 'support', label: 'Support',            iconKey: 'LifeBuoy',  tone: 'blue',   value: 'support@kalkiai.trade',  hint: 'Existing customers · platform + billing' },
  { id: 'partner', label: 'Partnerships',       iconKey: 'Handshake', tone: 'purple', value: 'partners@kalkiai.trade', hint: 'Reseller, integration, education' },
  { id: 'press',   label: 'Press & Media',      iconKey: 'Megaphone', tone: 'rose',   value: 'press@kalkiai.trade',    hint: 'Analyst briefings, media relations' },
  { id: 'legal',   label: 'Security & Legal',    iconKey: 'ShieldCheck', tone: 'green', value: 'legal@kalkiai.trade',    hint: 'Vendor onboarding, DPA, security packet' },
  { id: 'hq',      label: 'Headquarters',        iconKey: 'MapPin',    tone: 'cyan',   value: 'Bengaluru, India',    hint: 'Global operating base' },
]

export const SOCIALS = [
  { id: 'linkedin', label: 'LinkedIn', iconKey: 'Linkedin', href: 'https://www.linkedin.com/' },
  { id: 'x',        label: 'X',         iconKey: 'Twitter', href: 'https://x.com/' },
  { id: 'youtube',  label: 'YouTube',   iconKey: 'Youtube', href: 'https://www.youtube.com/' },
  { id: 'github',   label: 'GitHub',    iconKey: 'Github',  href: 'https://github.com/' },
]

// ── §9.9 Contact-page FAQ (6 items) ──────────────────────────────────
export const CONTACT_FAQ = [
  { q: 'How quickly can I launch?',                a: 'A fresh workspace is typically provisioned in under 90 seconds, and most customers go live inside one working afternoon — configure branding, connect a domain and invite your team, and you\'re in production.' },
  { q: 'Which plan should I choose?',              a: 'Starter is best for pilots and validation.  Professional is our most-popular commercial launch tier.  Enterprise adds dedicated success and strategic partnership.  If unsure, book a demo and we will match the plan to your business model.' },
  { q: 'How are demos conducted?',                 a: 'Live over video, tailored to the topic you select — platform, AI, trading, white-label or enterprise licensing.  Typical demos run 30–45 minutes with time for Q&A.  We can add extra sessions with your architects, security or legal teams on request.' },
  { q: 'Do you provide onboarding?',                a: 'Yes.  Professional and Enterprise plans include structured onboarding — brand configuration, domain setup, user provisioning and go-live checklists.  Enterprise adds a dedicated success manager for the first quarter.' },
  { q: 'How do I contact support?',                 a: 'Existing customers open a ticket from inside their workspace — tickets are prioritised by plan tier.  You can also reach us at support@kalkiai.trade for account-level questions.' },
  { q: 'Can I request a custom deployment?',        a: 'Yes.  Multi-region hosting, data residency, single-tenant deployments, custom SLAs and bespoke integrations are all available under Enterprise Consulting.  Talk to us early — we scope, plan and quote together.' },
]

// ── §9.10 Response commitments ────────────────────────────────────────
export const RESPONSE_SLAS = [
  { id: 'sales',    label: 'Sales enquiries',     iconKey: 'Handshake', tone: 'orange', sla: '1 business day',    body: 'Full commercial reply from a named account executive.' },
  { id: 'demo',     label: 'Demo requests',        iconKey: 'Calendar',  tone: 'green',  sla: '4 business hours',   body: 'Calendar options for your selected topic and timezone.' },
  { id: 'support',  label: 'Support (Pro/Ent)',    iconKey: 'LifeBuoy',   tone: 'blue',   sla: '4 business hours',   body: 'Priority-lane response for existing customers on Professional / Enterprise.' },
  { id: 'support2', label: 'Support (Starter)',    iconKey: 'LifeBuoy',   tone: 'purple', sla: '1 business day',     body: 'Standard support lane for Starter plan customers.' },
  { id: 'partner',  label: 'Partnership queries',   iconKey: 'Puzzle',    tone: 'cyan',   sla: '2 business days',    body: 'Deal review and next-step framing with the partnerships team.' },
  { id: 'press',    label: 'Press & media',         iconKey: 'Megaphone', tone: 'rose',   sla: '2 business days',    body: 'Verified statement, quote or interview scheduling.' },
]

export const OFFICE_HOURS = {
  hq:   'Global HQ',
  tz:   'UTC',
  days: 'Monday – Friday',
  time: '09:00 – 18:00 UTC',
  note: 'Support ticket lanes are 24×7 for Enterprise; SLA windows above reflect standard business-hour commitments.',
}
